<template>
  <NuxtLayout name="default" :show-back-button="false">
    <SofaxBreadcrumb :title="t('cookies.title')" :items="[{ name: t('cookies.title') }]" />

    <section class="sofax bg-white dark:bg-gray-950">
      <div class="section-space">
        <div class="shell">
          <div class="rich-text-block">
            <p>{{ t('cookies.intro') }}</p>

            <h2>{{ t('cookies.tracking.title') }}</h2>
            <div class="card my-8">
              <div class="flex flex-wrap items-center justify-between gap-6">
                <div class="min-w-0 flex-1">
                  <div class="font-title text-xl font-bold text-ink dark:text-white">{{ t('cookies.tracking.label') }}</div>
                  <div class="mt-1 text-base text-muted dark:text-gray-400">
                    {{ t('cookies.tracking.description') }}
                  </div>
                </div>

                <!-- The template's lilac switch; the real checkbox stays for the existing handler. -->
                <label class="group inline-flex cursor-pointer items-center gap-3">
                  <input
                    type="checkbox"
                    class="peer sr-only"
                    :checked="trackingEnabled"
                    :aria-label="t('cookies.tracking.toggle')"
                    @change="toggleTracking($event)"
                  >
                  <span
                    class="relative inline-block h-[1.875rem] w-14 shrink-0 rounded-full transition-colors duration-300 peer-focus-visible:ring-2 peer-focus-visible:ring-accent peer-focus-visible:ring-offset-2"
                    :class="trackingEnabled ? 'bg-lilac' : 'bg-line dark:bg-gray-700'"
                  >
                    <span
                      class="absolute left-[4px] top-1/2 flex size-[1.375rem] -translate-y-1/2 items-center justify-center rounded-full bg-ink shadow transition-transform duration-300 dark:bg-white"
                      :class="trackingEnabled ? 'translate-x-[1.625rem]' : 'translate-x-0'"
                    />
                  </span>
                </label>
              </div>

              <SofaxNotice v-if="!trackingEnabled" type="warning" class="mt-5">
                {{ t('cookies.tracking.disabled') }}
              </SofaxNotice>
            </div>

            <h2>{{ t('cookies.known.title') }}</h2>
            <ul>
              <li><code>visitor_id</code> (localStorage) – {{ t('cookies.known.visitorId') }}</li>
              <li><code>analytics_tracking_enabled</code> (localStorage) – {{ t('cookies.known.analyticsPref') }}</li>
              <li><code>session_id</code> (sessionStorage) – {{ t('cookies.known.sessionId') }}</li>
              <li><code>session_start</code> (sessionStorage) – {{ t('cookies.known.sessionStart') }}</li>
              <li><code>imr_ref</code> (cookie) – {{ t('cookies.known.affiliateRef') }}</li>
            </ul>
            <p class="!text-base !text-muted dark:!text-gray-400">
              {{ t('cookies.known.thirdParty') }}
            </p>

            <h2>{{ t('cookies.current.cookies') }}</h2>
            <div class="overflow-x-auto">
              <table>
                <thead>
                  <tr>
                    <th>{{ t('cookies.current.name') }}</th>
                    <th>{{ t('cookies.current.value') }}</th>
                    <th>{{ t('cookies.current.domain') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="cookies.length === 0">
                    <td colspan="3" class="text-muted dark:text-gray-400">{{ t('cookies.current.none') }}</td>
                  </tr>
                  <tr v-for="c in cookies" :key="c.name">
                    <td><code>{{ c.name }}</code></td>
                    <td class="break-all">{{ c.valuePreview }}</td>
                    <td>{{ c.domain || siteDomain }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h2>{{ t('cookies.storage.title') }}</h2>
            <div class="grid gap-6 md:grid-cols-2">
              <div>
                <h3>localStorage</h3>
                <div class="overflow-x-auto">
                  <table>
                    <thead>
                      <tr>
                        <th>{{ t('cookies.storage.key') }}</th>
                        <th>{{ t('cookies.storage.value') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="localItems.length === 0">
                        <td colspan="2" class="text-muted dark:text-gray-400">{{ t('cookies.storage.empty') }}</td>
                      </tr>
                      <tr v-for="i in localItems" :key="i.key">
                        <td><code>{{ i.key }}</code></td>
                        <td class="break-all">{{ i.value }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div>
                <h3>sessionStorage</h3>
                <div class="overflow-x-auto">
                  <table>
                    <thead>
                      <tr>
                        <th>{{ t('cookies.storage.key') }}</th>
                        <th>{{ t('cookies.storage.value') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="sessionItems.length === 0">
                        <td colspan="2" class="text-muted dark:text-gray-400">{{ t('cookies.storage.empty') }}</td>
                      </tr>
                      <tr v-for="i in sessionItems" :key="i.key">
                        <td><code>{{ i.key }}</code></td>
                        <td class="break-all">{{ i.value }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div class="mt-10">
              <SofaxBtn variant="dark" :label="t('cookies.clear')" @click="clearKnownAnalytics()" />
            </div>
          </div>
        </div>
      </div>
      <div class="separator" />
    </section>

    <SofaxPageCta />
  </NuxtLayout>
</template>

<script setup lang="ts">
const { t } = useI18n()

// SEO Meta
useSeoMeta({
  title: () => t('seo.cookies.title'),
  description: () => t('seo.cookies.description'),
  ogTitle: () => t('seo.cookies.title'),
  ogDescription: () => t('seo.cookies.description'),
})

const trackingEnabled = ref(true)
const siteDomain = useRuntimeConfig().public.websiteBaseUrl || (typeof window !== 'undefined' ? window.location.hostname : '')
const cookies = ref<{ name: string; valuePreview: string; domain?: string }[]>([])
const localItems = ref<{ key: string; value: string }[]>([])
const sessionItems = ref<{ key: string; value: string }[]>([])

const refresh = () => {
  // Cookies
  const raw = document.cookie || ''
  cookies.value = raw
    .split(';')
    .map(c => c.trim())
    .filter(Boolean)
    .map((c) => {
      const [name, ...rest] = c.split('=')
      const value = rest.join('=')
      return {
        name,
        valuePreview: value.length > 64 ? value.slice(0, 64) + '…' : value,
      }
    })

  // localStorage
  localItems.value = []
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (!key) continue
    localItems.value.push({ key, value: localStorage.getItem(key) ?? '' })
  }

  // sessionStorage
  sessionItems.value = []
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i)
    if (!key) continue
    sessionItems.value.push({ key, value: sessionStorage.getItem(key) ?? '' })
  }

  trackingEnabled.value = localStorage.getItem('analytics_tracking_enabled') !== 'false'
}

const clearKnownAnalytics = () => {
  localStorage.removeItem('visitor_id')
  sessionStorage.removeItem('session_id')
  sessionStorage.removeItem('session_start')
  refresh()
}

const toggleTracking = (e: Event) => {
  const checked = (e.target as HTMLInputElement).checked
  if (!checked) {
    localStorage.setItem('analytics_tracking_enabled', 'false')
    clearKnownAnalytics()
  } else {
    localStorage.removeItem('analytics_tracking_enabled')
    // Switching tracking on here is a positive choice, like the banner's "Accept".
    localStorage.setItem('cookie_banner_accepted', 'true')
  }
  trackingEnabled.value = checked
  // Also removes (or allows) the affiliate referral cookie.
  announceCookieConsent(checked)
}

/*
 * `layout: false` + explicit NuxtLayout so the default layout's back button
 * can be switched off; definePageMeta cannot pass layout props.
 */
definePageMeta({ layout: false, auth: false })

onMounted(() => {
  refresh()
})
</script>
